import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DemoContextType, UserRole, DemoUser, TourStep, TourPath } from '../types/demo';
import { DEMO_USERS, getRoleDisplayName } from '../data/demoData';
import { TOUR_PATHS, getTourPathForRole } from '../data/tourPaths';
import { showToast } from '../utils/notifications';

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export const useDemo = (): DemoContextType => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};

export const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Core demo state
  const [isDemoMode, setIsDemoMode] = useState<boolean>(() => {
    return localStorage.getItem('demo-mode') === 'true';
  });
  
  const [currentDemoRole, setCurrentDemoRole] = useState<UserRole | null>(() => {
    const saved = localStorage.getItem('demo-role');
    return saved ? (saved as UserRole) : null;
  });

  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Tour state
  const [isOnTour, setIsOnTour] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const [tourSteps, setTourSteps] = useState<TourStep[]>([]);

  // Progress tracking
  const [demoProgress, setDemoProgress] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem('demo-progress');
    return saved ? JSON.parse(saved) : {};
  });

  const navigate = useNavigate();

  // Available roles for demo
  const availableRoles: UserRole[] = Object.keys(DEMO_USERS) as UserRole[];

  // Get current demo user
  const demoUser: DemoUser | null = currentDemoRole ? DEMO_USERS[currentDemoRole] : null;

  // Persist demo state
  useEffect(() => {
    localStorage.setItem('demo-mode', isDemoMode.toString());
    if (currentDemoRole) {
      localStorage.setItem('demo-role', currentDemoRole);
    } else {
      localStorage.removeItem('demo-role');
    }
    localStorage.setItem('demo-progress', JSON.stringify(demoProgress));
  }, [isDemoMode, currentDemoRole, demoProgress]);

  const enterDemoMode = () => {
    setIsDemoMode(true);
    setShowWelcomeModal(true);
    showToast('Demo mode activated! Select a role to begin your guided tour.', 'info');
  };

  const exitDemoMode = () => {
    setIsDemoMode(false);
    setCurrentDemoRole(null);
    setIsOnTour(false);
    setCurrentTourStep(0);
    setTourSteps([]);
    setShowWelcomeModal(false);
    localStorage.removeItem('demo-mode');
    localStorage.removeItem('demo-role');
    localStorage.removeItem('demo-progress');
    navigate('/login');
    showToast('Demo mode ended. You have been logged out.', 'info');
  };

  const switchDemoRole = (role: UserRole) => {
    const previousRole = currentDemoRole;
    setCurrentDemoRole(role);
    
    // Always start a new tour when switching roles
    startTourWithRole(role);
    
    if (previousRole) {
      showToast(`Role switched to ${getRoleDisplayName(role)}`, 'success');
    } else {
      showToast(`Started demo as ${getRoleDisplayName(role)}`, 'success');
    }
  };

  const startTourWithRole = (role: UserRole) => {
    const tourPath = getTourPathForRole(role);
    setTourSteps(tourPath.steps);
    setCurrentTourStep(0);
    setIsOnTour(true);
    setShowWelcomeModal(false);

    // Navigate to the first step if it has a page
    const firstStep = tourPath.steps[0];
    if (firstStep?.page && firstStep.action === 'navigate') {
      navigate(firstStep.actionTarget || firstStep.page);
    }

    showToast(`Started ${tourPath.title}`, 'info', 4000);
  };

  const restartCurrentTour = () => {
    if (currentDemoRole) {
      startTourWithRole(currentDemoRole);
      showToast('Tour restarted from the beginning', 'info');
    }
  };

  const nextTourStep = () => {
    if (currentTourStep < tourSteps.length - 1) {
      const nextStep = tourSteps[currentTourStep + 1];
      setCurrentTourStep(prev => prev + 1);
      
      // Handle navigation for the next step
      if (nextStep.page && nextStep.action === 'navigate') {
        navigate(nextStep.actionTarget || nextStep.page);
      }
    } else {
      // Tour completed
      setIsOnTour(false);
      setCurrentTourStep(0);
      showToast('Tour completed! You can continue exploring freely.', 'success', 4000);
    }
  };

  const previousTourStep = () => {
    if (currentTourStep > 0) {
      const prevStep = tourSteps[currentTourStep - 1];
      setCurrentTourStep(prev => prev - 1);
      
      // Handle navigation for the previous step
      if (prevStep.page && prevStep.action === 'navigate') {
        navigate(prevStep.actionTarget || prevStep.page);
      }
    }
  };

  const skipTour = () => {
    setIsOnTour(false);
    setCurrentTourStep(0);
    showToast('Tour skipped. Explore at your own pace!', 'info');
  };

  const markStepCompleted = (stepId: string) => {
    setDemoProgress(prev => ({
      ...prev,
      [stepId]: true
    }));
  };

  const recordPageVisit = (page: string) => {
    setDemoProgress(prev => ({
      ...prev,
      pagesVisited: [...(prev.pagesVisited || []), page]
    }));
  };

  const setWelcomeModalOpen = (open: boolean) => {
    setShowWelcomeModal(open);
  };

  const value: DemoContextType = {
    isDemoMode,
    currentDemoRole,
    demoUser,
    availableRoles,
    demoProgress,
    
    // Tour system
    isOnTour,
    currentTourStep,
    tourSteps,
    showWelcomeModal,
    
    // Actions
    switchDemoRole,
    enterDemoMode,
    exitDemoMode,
    markStepCompleted,
    recordPageVisit,
    
    // Tour actions
    startTourWithRole,
    restartCurrentTour,
    nextTourStep,
    previousTourStep,
    skipTour,
    setWelcomeModalOpen
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

// Hook for demo database operations
export const useDemoDatabase = () => {
  const { isDemoMode } = useDemo();
  
  const showDemoMessage = (message: string, type: 'success' | 'info' | 'warning' = 'success') => {
    showToast(`[Demo Mode] ${message}`, type);
  };

  return {
    isDemoMode,
    showDemoMessage
  };
}; 